import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Download, Calendar as CalendarIcon } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingForm = ({ selectedDate, courseTitle, onSuccess }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    house_number: '',
    zip_code: '',
    city: '',
    referred_by: ''
  });
  const [calendarData, setCalendarData] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast({
        title: "Fehler",
        description: "Bitte wähle zuerst einen Termin aus.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/bookings`, {
        ...formData,
        course_date_id: selectedDate.id
      });

      setCalendarData(response.data.calendar_data);

      toast({
        title: "Buchung erfolgreich!",
        description: response.data.message
      });

      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      toast({
        title: "Fehler bei der Buchung",
        description: error.response?.data?.detail || "Ein Fehler ist aufgetreten. Bitte versuche es erneut.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadIcal = () => {
    if (calendarData?.ics_file) {
      const blob = new Blob([calendarData.ics_file], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${courseTitle}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (calendarData) {
    return (
      <Card className="border-2 border-green-500 max-w-full overflow-hidden">
        <CardHeader className="bg-green-50">
          <CardTitle className="text-green-800 text-center text-lg sm:text-xl">
            ✓ Buchung erfolgreich!
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 sm:pt-6 space-y-3 sm:space-y-4 px-3 sm:px-6">
          <p className="text-center text-gray-700 text-sm sm:text-base break-words">
            Eine Bestätigungsmail wurde an <strong className="break-all">{formData.email}</strong> gesendet.
          </p>

          <div className="bg-[#FFFBF0] p-3 sm:p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-center text-sm sm:text-base">
              Füge den Termin zu deinem Kalender hinzu
            </h4>
            <div className="space-y-2">
              <Button
                onClick={downloadIcal}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white text-xs sm:text-sm py-2 sm:py-3"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                <span className="truncate">Apple, Outlook, Android</span>
              </Button>
              
              <Button
                onClick={() => window.open(calendarData.google_calendar_url, '_blank')}
                variant="outline"
                className="w-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white text-xs sm:text-sm py-2 sm:py-3"
              >
                <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                <span className="truncate">Google Calendar</span>
              </Button>
            </div>
          </div>

          <p className="text-xs sm:text-sm text-gray-600 text-center">
            Wir freuen uns auf deine Teilnahme!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base sm:text-lg">Anmeldeformular</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name" className="text-sm">Vorname *</Label>
              <Input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder="Dein Vorname"
                className="text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name" className="text-sm">Nachname *</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder="Dein Nachname"
                className="text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm">E-Mail *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="deine@email.de"
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm">Telefon *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="+49 ..."
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="street" className="text-sm">Straße *</Label>
              <Input
                id="street"
                name="street"
                value={formData.street}
                onChange={handleChange}
                required
                placeholder="Musterstraße"
                className="text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="house_number" className="text-sm">Hausnr. *</Label>
              <Input
                id="house_number"
                name="house_number"
                value={formData.house_number}
                onChange={handleChange}
                required
                placeholder="1a"
                className="text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="zip_code" className="text-sm">PLZ *</Label>
              <Input
                id="zip_code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                required
                placeholder="86150"
                className="text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm">Ort *</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                placeholder="Augsburg"
                className="text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="referred_by" className="text-sm">Empfohlen von (optional)</Label>
            <Input
              id="referred_by"
              name="referred_by"
              value={formData.referred_by}
              onChange={handleChange}
              placeholder="Name des Empfehlers"
              className="text-sm"
            />
            <p className="text-xs text-gray-600">
              Bei Fortgeschrittenen-Kursen: 200€ Rabatt für Empfehlungen
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white shadow-lg hover:shadow-xl transition-all duration-300 text-base sm:text-lg py-4 sm:py-6"
          >
            {loading ? 'Wird gebucht...' : 'Verbindlich buchen'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
